/**
 * V2 PaSurveyTerminatedPage
 * Survey terminated/completed page
 */

import { memo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { Enum_Survey_Access_Restriction, Enum_Translation_Locale, notEmpty } from '@cyren/common-lib';
import { Button } from '@mui/material';
import { useStaticLocales } from '../../../../admin/hooks/locales/use-static-locale';
import Footer from '../../../../app/Footer';
import LoadingAnim from '../../../../components/LoadingAnim';
import LogoPatientDynamic from '../../../../components/LogoPatientDynamic';
import PaTreeHeader from './components/PaTreeHeader';
import SurveyContainer from './SurveyContainer';
import thumbsUp from '../../../../assets/ill/Thumbs_up.svg';
import { useSurveyMeta } from '../../../hooks/use-survey-meta';
import { useTranslations } from '../../../../patient/hooks/use-translations';
import { usePatientTransData } from '../../../hooks/use-patient-trans-data';
import type { TOptions } from '../../../../patient/pa-prop-types';
import { usePatientLocale } from '../../../hooks/use-patient-locale';

const collapCls =
  'collapse collapse-arrow border-2 border-black border-opacity-10 rounded-none first:rounded-t-xl last:rounded-b-xl border-t-0 first:border-t-2 font-base';
const collapHeaderCls = 'collapse-title text-base font-medium';
const collapBodyCls = 'collapse-content opacity-70 text-base';
const FAQ_META_CACHE_KEY = 'v2-pa-faq-meta-cache';

type TerminationReason = 'completed' | 'timeout' | 'with_staff';

/**
 * Page Content Component
 */
function PaSurveyTerminatedPageContent() {
  const nav = useNavigate();
  const params = useParams();
  const { localeKey, localeCode: patientLocaleCode } = usePatientLocale();
  const lockedLocale = `${localeKey || 'english'}`.toLowerCase();
  const lockedLocaleCode = patientLocaleCode;
  const { sltStr } = useStaticLocales({ localeCode: lockedLocaleCode });
  const [, { t }] = useTranslations({ locale: lockedLocale as Enum_Translation_Locale });

  // Get all data from Container
  const {
    isLoading,
    surveyState,
    reportState,
    survey,
    visit,
    org,
  } = SurveyContainer.useContainer();

  // Local state
  const [selectedFaqIndex, setSelectedFaqIndex] = useState<number | null>(null);
  const [terminationReason, setTerminationReason] = useState<TerminationReason>('completed');
  const [cachedMetaItems, setCachedMetaItems] = useState<Array<{ id?: string; qnaQuestionKey: string; qnaAnswerKey: string }>>([]);
  const { transKeys, metaItems } = useSurveyMeta({
    surveyId: survey?.id ? String(survey.id) : undefined,
    surveyShortId: params.shortId,
    metaKey: 'faq',
  });
  const effectiveMetaItems = notEmpty(metaItems) ? metaItems : cachedMetaItems;
  const effectiveTransKeys = notEmpty(metaItems)
    ? transKeys
    : effectiveMetaItems.flatMap((item) => [item.qnaQuestionKey, item.qnaAnswerKey]).filter(notEmpty);
  const tOptions = (reportState?.systemValueMap
    ? Object.fromEntries(
      Object.entries(reportState.systemValueMap).filter(([, value]) => typeof value === 'string')
    )
    : {}) as TOptions;
  usePatientTransData({
    keys: effectiveTransKeys,
    locale: lockedLocale as Enum_Translation_Locale,
  });

  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(FAQ_META_CACHE_KEY);
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        setCachedMetaItems(parsed);
      }
    } catch (error) {
      console.warn('Failed to restore cached faq meta items:', error);
    }
  }, []);

  useEffect(() => {
    if (!notEmpty(metaItems)) return;
    try {
      sessionStorage.setItem(FAQ_META_CACHE_KEY, JSON.stringify(metaItems));
    } catch (error) {
      console.warn('Failed to cache faq meta items:', error);
    }
  }, [metaItems]);

  // Determine termination reason
  useEffect(() => {
    if (surveyState.encounterCheckReason === 'timeout') {
      setTerminationReason('timeout');
    } else if (surveyState.encounterCheckReason === 'with_staff') {
      setTerminationReason('with_staff');
    } else if (visit?.status === 'done' || visit?.archived) {
      setTerminationReason('completed');
    }
  }, [surveyState.encounterCheckReason, visit?.status, visit?.archived]);

  // Loading state
  if (isLoading) {
    return (
      <div className='flex flex-1 w-full'>
        <LoadingAnim className='h-full p-10 w-full' />
      </div>
    );
  }

  // Render message based on termination reason
  const renderMessage = () => {
    if (terminationReason === 'timeout') {
      return (
        <div className='flex-center flex-col gap-4'>
          <div className='pt-12 pb-6 w-full flex justify-center'>
            <img src={thumbsUp} alt='Thumbs Up' className='block mx-auto max-w-[180px] w-full h-auto' />
          </div>
          <div className='flex-col gap-2 flex-center-y'>
            <h4 className='font-bold sm'>{sltStr({ key: 'terminated-timeout-title' })}</h4>
            <div className='text-base opacity-70 px-2 text-center'>
              {sltStr({ key: 'terminated-timeout-desc' })}
            </div>
          </div>
        </div>
      );
    }

    if (terminationReason === 'with_staff') {
      return (
        <div className='flex-center flex-col gap-4'>
          <div className='pt-12 pb-6 w-full flex justify-center'>
            <img src={thumbsUp} alt='Thumbs Up' className='block mx-auto max-w-[180px] w-full h-auto' />
          </div>
          <div className='flex-col gap-2 flex-center-y'>
            <h4 className='font-bold sm'>{sltStr({ key: 'terminated-with-staff-title' })}</h4>
            <div className='text-base opacity-70 px-2 text-center'>
              {sltStr({ key: 'terminated-with-staff-desc' })}
            </div>
          </div>
        </div>
      );
    }

    // Default completed message
    return (
      <div className='flex-center flex-col gap-4'>
        {!visit?.archived && (
          <>
            <div className='pt-12 pb-6 w-full flex justify-center'>
              <img src={thumbsUp} alt='Thumbs Up' className='block mx-auto max-w-[180px] w-full h-auto' />
            </div>
            <div className='flex-col gap-2 flex-center-y'>
              <h4 className='font-bold sm'>{sltStr({ key: 'label-thank-you' })}</h4>
              <div className='text-base opacity-70 px-2 text-center'>
                {sltStr({ key: 'pa-survey-end-desc' })}
              </div>
            </div>
          </>
        )}

        {visit?.archived && (
          <div className='flex-center flex-col gap-4 py-10'>
            <div className='flex-col gap-2 flex-center-y'>
              <div className='text-base opacity-70 px-2 text-center flex-1 bg-primary bg-opacity-10 rounded-xl w-full p-2'>
                {sltStr({ key: 'finished-archived' })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Check if provider report should be accessible
  const canViewProviderReport =
    survey?.access_restriction &&
    survey.access_restriction !== Enum_Survey_Access_Restriction.ProviderOnly;

  return (
    <div className='flex-1 flex-col gap-5 justify-between'>
      <div className='flex-1 flex-col gap-5'>
        <PaTreeHeader showFullBar noBack logoUrl={org?.logoUrl} />

        <div className='px-5'>
          <div className='flex-center'>
            <LogoPatientDynamic logoUrl={org?.logoUrl} className='h-6 w-auto' />
          </div>

          {renderMessage()}

          {/* View Provider Report Button */}
          {canViewProviderReport && (
            <div className='flex-center flex-col gap-4 py-10'>
              <div className='flex-col gap-2 flex-center-y'>
                <Button
                  variant='contained'
                  onClick={() => nav(`/sp/${visit?.uuid}`)}
                >
                  {sltStr({ key: 'btn-view-provider-report' })}
                </Button>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {notEmpty(effectiveMetaItems) && (
            <div className='flex-col py-4 w-full'>
              {effectiveMetaItems.map((item, idx) => {
                const open = selectedFaqIndex === idx;
                return (
                  <div
                    key={item.id ?? `${item.qnaQuestionKey}-${idx}`}
                    className={classNames(collapCls, open && 'collapse-open')}
                    onClick={() => setSelectedFaqIndex(open ? null : idx)}
                  >
                    <div className={collapHeaderCls}>
                      {t(item.qnaQuestionKey, tOptions) ?? ''}
                    </div>
                    <div className={collapBodyCls}>
                      <p>{t(item.qnaAnswerKey, tOptions) ?? ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className='hidden flex-none'>
        <Footer />
      </div>
    </div>
  );
}

/**
 * Main Page Component
 */
function PaSurveyTerminatedPage() {
  return (
    <SurveyContainer.Provider>
      <PaSurveyTerminatedPageContent />
    </SurveyContainer.Provider>
  );
}

export default memo(PaSurveyTerminatedPage);
